import {
  Application,
  BankApplication,
  CustomerApplicationStatusEnum,
  Query,
  Typenames,
  WhereToGoDestination,
  WhereToGoDestinationTypenames,
} from '@varo/graphql-schema-types';
import { Href, router } from 'expo-router';
import { useContext, useEffect, useRef, useState } from 'react';
import CustomerApplicationOrigination from 'services/graphql/queries/CustomerApplicationOrigination';
import GetSsnExists from 'services/graphql/queries/GetSsnExists';
import WhereToGo from 'services/graphql/queries/WhereToGo';
import { isE2E } from 'shared/helpers/isE2E';
import { AbortError } from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import useCancellableQuery from 'shared/hooks/useCancellableQuery/useCancellableQuery';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';
import { RequestPolicy } from 'urql';

function logWhereToGoResult(nextRoute: string) {
  logBreadcrumb({
    level: 'info',
    message: `Result of whereToGo navigates to ${nextRoute}`,
  });
}

async function executeOnboardingQuery(
  executeQuery: (
    variables: Record<string, never>,
    context: { requestPolicy: RequestPolicy },
  ) => Promise<Query>,
): Promise<Query | undefined> {
  try {
    return await executeQuery({}, { requestPolicy: 'cache-first' });
  } catch (error) {
    if (error instanceof AbortError) {
      throw error;
    }
    return undefined;
  }
}

function useWhereToGo(shouldNavigate = true) {
  const { isLoggedIn } = useContext<AuthContextProps>(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [initiateWhereToGo, setInitiateWhereToGo] = useState(false);
  const [requestOptions, setRequestOptions] = useState<{
    requestPolicy?: RequestPolicy;
  }>();
  const [whereToGoDestination, setWhereToGoDestination] =
    useState<WhereToGoDestination>();
  const [executeWhereToGo] = useCancellableQuery<Query>(WhereToGo);
  const [executeGetSsnExists] = useCancellableQuery<Query>(GetSsnExists);
  const [executeCustomerApplicationOrigination] = useCancellableQuery<Query>(
    CustomerApplicationOrigination,
    { allowPartialData: true },
  );
  const latestRequestIdRef = useRef(0);

  const fetchWhereToGo = async (options?: {
    requestPolicy?: RequestPolicy;
  }) => {
    logBreadcrumb({
      level: 'info',
      message: 'Initiating whereToGo fetch',
    });

    latestRequestIdRef.current += 1;
    const requestId = latestRequestIdRef.current;
    setIsLoading(true);

    try {
      const whereToGoData = await executeWhereToGo(
        {},
        { requestPolicy: options?.requestPolicy ?? 'cache-first' },
      );
      const whereToGoTypename = whereToGoData.whereToGo?.__typename;
      let nextRoute = '/';

      switch (whereToGoTypename) {
        case WhereToGoDestinationTypenames.GoToDecision: {
          const bankApplicationStatus =
            whereToGoData.whereToGo?.bankApplicationStatus;
          switch (bankApplicationStatus) {
            case CustomerApplicationStatusEnum.Approved:
              nextRoute = '/';
              break;
            case CustomerApplicationStatusEnum.Declined:
            case CustomerApplicationStatusEnum.InReview:
            case CustomerApplicationStatusEnum.Pending:
            default:
              nextRoute = '/signup/application-decision';
          }

          logWhereToGoResult(nextRoute);

          router.replace(nextRoute as Href);
          break;
        }
        case WhereToGoDestinationTypenames.GoToOnboarding:
          {
            const getSSNExist =
              await executeOnboardingQuery(executeGetSsnExists);
            const response = await executeOnboardingQuery(
              executeCustomerApplicationOrigination,
            );
            let customerApplication: BankApplication | undefined;
            response?.applications?.forEach((app: Application) => {
              if (app !== null) {
                if (
                  '__typename' in app &&
                  app.__typename === Typenames.BankApplication
                ) {
                  customerApplication = app as BankApplication;
                }
              }
            });

            if (
              customerApplication &&
              customerApplication?.eligibleForReapply !== false
            ) {
              switch (customerApplication.status) {
                case CustomerApplicationStatusEnum.ReadyToSubmit:
                  nextRoute = '/signup/legal';
                  break;
                case CustomerApplicationStatusEnum.Incomplete:
                  if (
                    !customerApplication.firstName ||
                    !customerApplication.lastName
                  ) {
                    nextRoute = '/signup/personal-information';
                    break;
                  } else if (!(
                    customerApplication.mailingAddress ||
                    customerApplication.address?.addressLine1
                  )) {
                    nextRoute = '/signup/address-collection';
                    break;
                  } else if (!customerApplication.dateOfBirth) {
                    nextRoute = '/signup/dob';
                    break;
                  } else if (!customerApplication.occupation) {
                    nextRoute = '/signup/employment';
                    break;
                  } else if (!customerApplication.countryOfCitizenship) {
                    nextRoute = '/signup/citizenship';
                    break;
                  } else if (!getSSNExist?.user?.customerInfo?.ssnExists) {
                    nextRoute = '/signup/verify-ssn';
                    break;
                  } else {
                    nextRoute = '/signup/legal';
                    break;
                  }
                case CustomerApplicationStatusEnum.Pending:
                  nextRoute = '/signup/application-decision';
                  break;
                case CustomerApplicationStatusEnum.InReview:
                  nextRoute = '/signup/application-decision';
                  break;
                case CustomerApplicationStatusEnum.Declined:
                  nextRoute = '/signup/personal-information';
                  break;
                default:
                  return;
              }
              logWhereToGoResult(nextRoute);

              router.push(nextRoute as Href);
            } else if (
              customerApplication?.status ===
                CustomerApplicationStatusEnum.Declined &&
              customerApplication.eligibleForReapply === false
            ) {
              nextRoute = '/signup/application-decision';

              logWhereToGoResult(nextRoute);

              router.push(nextRoute as Href);
            } else {
              nextRoute = '/signup/personal-information';

              logWhereToGoResult(nextRoute);

              router.push(nextRoute as Href);
            }
          }
          break;
        case WhereToGoDestinationTypenames.GoToAccountClosed:
        case WhereToGoDestinationTypenames.GoToAccountDeceased:
          nextRoute = '/reactivation';

          logWhereToGoResult(nextRoute);

          router.replace(nextRoute as Href);
          break;
        case WhereToGoDestinationTypenames.GoToIdvRequest:
          nextRoute = 'signup/application-decision';

          logWhereToGoResult(nextRoute);

          router.replace(nextRoute as Href);
          break;
        case WhereToGoDestinationTypenames.GoToEddRequest:
          nextRoute = 'signup/edd-questions';

          logWhereToGoResult(nextRoute);

          router.replace(nextRoute as Href);
          break;
        case WhereToGoDestinationTypenames.GoToNextBestActionV2:
        case WhereToGoDestinationTypenames.GoToIntentBasedOnboarding:
          if (!isE2E) {
            if (shouldNavigate) {
              logWhereToGoResult('/onboarding');

              router.replace({
                pathname: '/onboarding',
              });
            } else {
              setWhereToGoDestination(whereToGoData.whereToGo);
            }
          } else {
            router.replace('/');
          }
          break;
        default:
          logWhereToGoResult(nextRoute);

          router.replace(nextRoute as Href);
          break;
      }
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      }
      logWhereToGoResult('/');
      router.replace('/');
    } finally {
      if (requestId === latestRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (isLoggedIn && initiateWhereToGo) {
      void fetchWhereToGo(requestOptions);
      setInitiateWhereToGo(false);
      setRequestOptions(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, initiateWhereToGo]);

  return {
    initiateWhereToGo: (options?: { requestPolicy?: RequestPolicy }) => {
      setIsLoading(true);
      setRequestOptions(options);
      setInitiateWhereToGo(true);
    },
    fetchWhereToGo,
    isLoading,
    whereToGoDestination,
  };
}
export default useWhereToGo;
